import ExcelJS from 'exceljs';
import { colorBySimilarity, textColorBySimilarity } from '../utils/colorUtils';
import { cssColorToArgb } from './hslToArgb';
import { HeatmapExportParams } from './heatmapExcelExport.types';

export function columnLetter(col: number): string {
    let n = col;
    let result = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        result = String.fromCharCode(65 + rem) + result;
        n = Math.floor((n - 1) / 26);
    }
    return result;
}

function addMetadataSheet(workbook: ExcelJS.Workbook, params: HeatmapExportParams): void {
    const { metadata, heatMapData } = params;
    const rows = heatMapData.length;
    const cols = heatMapData[0]?.length ?? 0;
    const sheet = workbook.addWorksheet('Метаданные');

    const entries: [string, string | number][] = [
        ['Документ A', metadata.documentTitleA],
        ['Документ B', metadata.documentTitleB],
        ['Тип свойства A', metadata.propertyTypeNameA],
        ['Тип свойства B', metadata.propertyTypeNameB],
        ['Дата экспорта', metadata.exportedAt.toLocaleString('ru-RU')],
        ['Размер матрицы', `${rows} × ${cols}`],
        ['Вес сходства A', metadata.weightA],
    ];

    entries.forEach(([label, value], i) => {
        sheet.getCell(`A${i + 1}`).value = label;
        sheet.getCell(`B${i + 1}`).value = value;
    });
    sheet.getColumn(1).width = 22;
    sheet.getColumn(2).width = 60;
}

function styleDataCell(cell: ExcelJS.Cell, sim: number): void {
    const bg = cssColorToArgb(colorBySimilarity(sim));
    const fg = cssColorToArgb(textColorBySimilarity(sim));
    cell.numFmt = '0.00';
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
    cell.font = { color: { argb: fg } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
}

function addHeatmapSheet(workbook: ExcelJS.Workbook, params: HeatmapExportParams): void {
    const { heatMapData, showAverages, calcSim } = params;
    if (heatMapData.length === 0 || heatMapData[0].length === 0) return;

    const sheet = workbook.addWorksheet('Тепловая карта');
    const numRows = heatMapData.length;
    const numCols = heatMapData[0].length;
    const dataStartRow = 2;
    const dataStartCol = 2; // B

    const lastDataCol = dataStartCol + numCols - 1;
    const lastDataRow = dataStartRow + numRows - 1;
    const avgCol = showAverages ? lastDataCol + 1 : -1;
    const avgRow = showAverages ? lastDataRow + 1 : -1;

    // Column headers (propB) — full text
    heatMapData[0].forEach((cell, j) => {
        const col = dataStartCol + j;
        const header = sheet.getCell(1, col);
        header.value = cell.propB.text;
        header.alignment = { wrapText: true, vertical: 'middle' };
        sheet.getColumn(col).width = Math.min(60, Math.max(12, cell.propB.text.length * 0.8));
    });

    if (showAverages) {
        const h = sheet.getCell(1, avgCol);
        h.value = 'Среднее';
        h.font = { bold: true };
    }

    // Rows
    heatMapData.forEach((row, i) => {
        const excelRow = dataStartRow + i;
        const rowHeader = sheet.getCell(excelRow, 1);
        rowHeader.value = row[0].propA.text;
        rowHeader.alignment = { wrapText: true, vertical: 'middle' };
        sheet.getColumn(1).width = Math.min(60, Math.max(12, row[0].propA.text.length * 0.8));

        row.forEach((cell, j) => {
            const excelCol = dataStartCol + j;
            const dataCell = sheet.getCell(excelRow, excelCol);
            const sim = calcSim(cell.similarity);
            dataCell.value = sim;
            styleDataCell(dataCell, sim);
        });

        if (showAverages) {
            const from = columnLetter(dataStartCol);
            const to = columnLetter(lastDataCol);
            const avgCell = sheet.getCell(excelRow, avgCol);
            avgCell.value = { formula: `AVERAGE(${from}${excelRow}:${to}${excelRow})` };
            avgCell.numFmt = '0.00';
            avgCell.font = { bold: true };
        }
    });

    if (showAverages) {
        const labelCell = sheet.getCell(avgRow, 1);
        labelCell.value = 'Среднее';
        labelCell.font = { bold: true };

        for (let j = 0; j < numCols; j++) {
            const excelCol = dataStartCol + j;
            const colL = columnLetter(excelCol);
            const avgCell = sheet.getCell(avgRow, excelCol);
            avgCell.value = {
                formula: `AVERAGE(${colL}${dataStartRow}:${colL}${lastDataRow})`,
            };
            avgCell.numFmt = '0.00';
            avgCell.font = { bold: true };
        }
    }
}

export async function buildHeatmapWorkbook(params: HeatmapExportParams): Promise<ExcelJS.Workbook> {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'STRAT AI';
    workbook.created = params.metadata.exportedAt;
    addMetadataSheet(workbook, params);
    addHeatmapSheet(workbook, params);
    return workbook;
}

function formatExportFilename(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `stratai-heatmap_${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}.xlsx`;
}

export function downloadXlsxBuffer(buffer: ArrayBuffer, filename: string): void {
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
}

export async function exportHeatmapToXlsx(params: HeatmapExportParams): Promise<void> {
    const workbook = await buildHeatmapWorkbook(params);
    const buffer = await workbook.xlsx.writeBuffer();
    downloadXlsxBuffer(buffer, formatExportFilename(params.metadata.exportedAt));
}
